import { useEffect, useState } from "react";
import { logAnalyticsAndHotjarEvent } from "../../main";
import { useAppSelector } from "../../app/hooks";
import useWindowWidth from "../../hooks/useWindowWidth";
import { ToastMessage } from "@allaria-tech/matter/ToastMessage";
import { Feedback } from "@allaria-tech/matter/Feedback";

import styles from "./InteractiveInformation.module.scss";

interface Button {
  text: string;
  onClick: () => void | Promise<void>;
  loading?: boolean;
  disabled?: boolean;
}

interface InformationProps {
  alertType: "INFO" | "ERROR";
  title: string;
  subtitle: string | React.ReactNode;
  description?: string;
  content?: React.ReactNode;
  primaryButton: Button;
  secondaryButton?: Button;
  /**
   * Matter's Feedback modal hardcodes `centerContentOnMobile`, which clips tall
   * bodies (checkbox below the fold) and blocks touch scroll. Opt in when the
   * children can outgrow the viewport — e.g. ACTIVE_ACCOUNT_SUMMARY.
   */
  allowOverflowScroll?: boolean;
}

const InteractiveInformation = ({
  alertType,
  title,
  subtitle,
  description,
  content,
  primaryButton,
  secondaryButton,
  allowOverflowScroll = false,
}: InformationProps) => {
  const { centeredCard, scrollableFeedback } = styles;

  const { type } = useAppSelector((state) => state.infoStep);

  const { width } = useWindowWidth();
  const presentation =
    width <= 768 ? ({ isOpen: true } as const) : ({ inline: true } as const);

  const [loading, setLoading] = useState({ primary: false, secondary: false });

  useEffect(() => {
    if (type === "PENDING_REGISTRY_LOCAL" || type === "PENDING_REGISTRY_REMOTE")
      logAnalyticsAndHotjarEvent("modal_returning_user_displayed");
  }, []);

  const handleClick = async (
    action: "primary" | "secondary",
    onClick: Button["onClick"],
  ) => {
    setLoading((prev) => ({ ...prev, [action]: true }));
    try {
      await onClick();
    } finally {
      setLoading((prev) => ({ ...prev, [action]: false }));
    }
  };

  return (
    <div className={centeredCard}>
      <Feedback
        {...presentation}
        type={alertType}
        title={title}
        description={typeof subtitle === "string" ? subtitle : undefined}
        className={allowOverflowScroll ? scrollableFeedback : undefined}
        primaryAction={{
          text: primaryButton.text,
          onClick: () => void handleClick("primary", primaryButton.onClick),
          isLoading: loading.primary || primaryButton.loading,
          isDisabled:
            primaryButton.disabled || loading.primary || loading.secondary,
        }}
        secondaryAction={
          secondaryButton?.text
            ? {
                text: secondaryButton.text,
                variant: "secondary",
                onClick: () =>
                  void handleClick("secondary", secondaryButton.onClick),
                isLoading: loading.secondary || secondaryButton.loading,
                isDisabled:
                  secondaryButton.disabled ||
                  loading.primary ||
                  loading.secondary,
              }
            : undefined
        }
      >
        <ToastMessage
          messageId="informationAlert"
          position="TOP"
          className="toastMessage"
        />
        {typeof subtitle !== "string" && subtitle}
        {description && <p>{description}</p>}
        {content}
      </Feedback>
    </div>
  );
};

export default InteractiveInformation;
